import { Capacitor } from '@capacitor/core'
import Storage from '@/lib/memory/Storage'
import { Share } from '@capacitor/share'
import { toPng, toBlob } from 'html-to-image'
import { Directory, Filesystem } from '@capacitor/filesystem'

const createCsvContent = (arrData = []) => {
    if (!Array.isArray(arrData) || arrData.length === 0) {
        return null
    }

    return [Object.keys(arrData[0]).join(';'), ...arrData.map((item) => Object.values(item).join(';'))]
        .join('\n')
        .replace(/(^\[)|(\]$)/gm, '')
}

const triggerCsvDownload = (csvContent, filenameWithExtension) => {
    const fileParams = 'data:text/csv;charset=utf-8,%EF%BB%BF'
    const data = fileParams + encodeURIComponent(csvContent)
    const link = document.createElement('a')
    link.setAttribute('href', data)
    link.setAttribute('target', '_blank')
    link.setAttribute('download', filenameWithExtension)
    link.click()
}

const canUseNativeShare = async () => {
    if (typeof Capacitor?.isNativePlatform !== 'function' || !Capacitor.isNativePlatform()) {
        return false
    }

    try {
        const shareState = await Share.canShare()
        return Boolean(shareState?.value)
    } catch {
        return false
    }
}

const isShareCanceledError = (error) => {
    const message = String(error?.message || error || '').toLowerCase()
    return message.includes('share canceled') || message.includes('share cancelled')
}

const saveFileToStorage = async (strData, filename = 'export', directory = Directory.Documents) => {
    const path = null
    const storageObject = new Storage()
    storageObject.directory(directory)
    await storageObject.writeFile(path, filename, strData)
    return await storageObject.getUri(filename)
}

const withCsvExtension = (filename = 'export') => {
    const normalizedFilename = filename || 'export'
    return normalizedFilename.toLowerCase().endsWith('.csv') ? normalizedFilename : `${normalizedFilename}.csv`
}

export const exportAndSaveCsvContent = async (csvContent, filename = 'export') => {
    if (!csvContent) return null

    const filenameWithExtension = withCsvExtension(filename)

    try {
        if (await canUseNativeShare()) {
            const sharedFile = await saveFileToStorage(csvContent, filenameWithExtension, Directory.Cache)

            await Share.share({
                title: filenameWithExtension,
                text: filenameWithExtension,
                url: sharedFile?.uri,
            })

            saveFileToStorage(csvContent, filenameWithExtension).catch((error) => {
                console.warn('[Export] Failed to persist CSV file after native share', error)
            })

            return sharedFile
        }

        triggerCsvDownload(csvContent, filenameWithExtension)
        return await saveFileToStorage(csvContent, filenameWithExtension)
    } catch (error) {
        if (isShareCanceledError(error)) {
            console.info('[Export] Native share canceled')
            return null
        }

        console.warn('[Export] Failed to export CSV file', error)

        try {
            return await saveFileToStorage(csvContent, filenameWithExtension)
        } catch (storageError) {
            console.warn('[Export] Failed to persist CSV file', storageError)
            return null
        }
    }
}

export const exportAndSaveCsv = async (arrData, filename = 'export') => {
    const csvContent = createCsvContent(arrData)
    return exportAndSaveCsvContent(csvContent, filename)
}

export const shareContent = async (node, filename = 'appFile', isMobile = true) => {
    const appTitle = import.meta.env.VITE_APP_TITLE
    const filenameWithExtension = `${filename}.png`

    if (isMobile && (await canUseNativeShare())) {
        const base64Data = await toPng(node)
        await Filesystem.writeFile({
            path: filenameWithExtension,
            data: base64Data,
            directory: Directory.Cache,
        })
        const uriResult = await Filesystem.getUri({
            directory: Directory.Cache,
            path: filenameWithExtension,
        })
        try {
            await Share.share({
                title: appTitle,
                text: appTitle,
                url: uriResult.uri,
            })
        } catch (error) {
            if (isShareCanceledError(error)) {
                console.info('[Export] Native share canceled')
                return null
            }
            throw error
        }
    } else {
        const blob = await toBlob(node)
        // Créer un lien de téléchargement
        if (navigator.msSaveBlob) {
            // Internet Explorer
            navigator.msSaveBlob(blob, filenameWithExtension)
        } else if ('download' in HTMLAnchorElement.prototype) {
            // Modern browsers
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = filenameWithExtension
            link.click()
            window.URL.revokeObjectURL(link.href)
        } else {
            console.error("Téléchargement d'image non pris en charge.")
        }
    }
}
